/**
 * Блок «Суд» (ТЗ п. 5.2.3, правило R-01).
 *
 * Суд предзаполняется по субъекту РФ адреса регистрации, но остаётся
 * доступным для ручного изменения в конкретном деле: дело может
 * рассматриваться не по общему правилу подсудности.
 */

import { useEffect, useState } from 'react';
import { api } from '../../../api.js';
import { Field, TextField } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';

interface CourtEntry {
  id: string;
  name: string;
  address?: string;
  regionCode?: string;
  /** Реквизиты сверены юристом с первоисточником */
  verified?: boolean;
  /** Справочная заметка о дополнительных зданиях и присутствиях */
  correspondence?: string;
}

export function CourtBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const [courts, setCourts] = useState<CourtEntry[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api
      .dictionary('courts', { limit: 200 })
      .then((r) => setCourts(r.entries as CourtEntry[]))
      .catch(() => setFailed(true));
  }, []);

  const selected = courts.find((c) => c.id === profile.court.id);

  const pick = (id: string) => {
    const court = courts.find((c) => c.id === id);
    if (!court) return;
    update((p) => ({
      ...p,
      court: { id: court.id, name: court.name, address: court.address ?? '', regionCode: court.regionCode },
    }));
  };

  return (
    <section className="block">
      <h2>Суд</h2>

      {failed && <p className="warning">Справочник судов недоступен — реквизиты можно ввести вручную.</p>}

      {/* Справочник заполняется постепенно: несверенный адрес лучше показать,
          чем молча подставить в заявление */}
      {selected && selected.verified !== true && (
        <p className="warning">
          Реквизиты этого суда не отмечены как сверенные с первоисточником. Проверьте наименование и адрес
          перед подачей.
        </p>
      )}

      <Field label="Арбитражный суд" required error={errorFor('court.name')}>
        <select value={profile.court.id ?? ''} disabled={readOnly} onChange={(e) => pick(e.target.value)}>
          <option value="">— выберите суд —</option>
          {courts.map((court) => (
            <option key={court.id} value={court.id}>
              {court.name}
            </option>
          ))}
        </select>
      </Field>

      <TextField
        label="Наименование в документе"
        value={profile.court.name}
        readOnly={readOnly}
        hint="Значение из справочника, доступно для правки в этом деле"
        onChange={(v) => update((p) => ({ ...p, court: { ...p.court, name: v } }))}
      />

      <TextField
        label="Почтовый адрес суда"
        value={profile.court.address}
        readOnly={readOnly}
        error={errorFor('court.address')}
        onChange={(v) => update((p) => ({ ...p, court: { ...p.court, address: v } }))}
      />

      {/* Заметка справочника: в документ не подставляется, решение за юристом */}
      {selected?.correspondence && (
        <p className="note">
          <strong>По этому суду есть уточнение:</strong> {selected.correspondence}
          <br />
          Если документы подаются по этому адресу, поправьте поле выше — правка коснётся только текущего дела.
        </p>
      )}
    </section>
  );
}
